import React, { useState, useEffect } from 'react';
import HeaderOne from "../components/HeaderTwo";
import BottomFooter from "../components/BottomFooter";

const AboutUs = () => {
    const [aboutData, setAboutData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedSections, setExpandedSections] = useState({
        story: true, // First section expanded by default
        services: false,
        hours: false
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchAboutData();
    }, []);

    const fetchAboutData = async () => {
        try {
            console.log('Fetching about us from API...');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/about-us/`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });

            console.log('Response status:', response.status);

            if (!response.ok) {
                throw new Error(`Failed to fetch about us: ${response.status}`);
            }

            const data = await response.json();
            console.log('Fetched about us:', data);

            setAboutData(data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching about data:', err);
            setError('Failed to load about information');
            setLoading(false);
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const toggleAllSections = (expand) => {
        setExpandedSections({
            story: expand,
            services: expand,
            hours: expand
        });
    };

    if (loading) {
        return (
            <>
                <HeaderOne />
                <div style={{
                    textAlign: "center",
                    padding: "50px",
                    color: "#8A7F8D",
                    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                    backgroundColor: "#FAF9F6",
                    minHeight: "400px"
                }}>
                    <p style={{ fontSize: "1.2rem" }}>Loading about information...</p>
                </div>
                <BottomFooter />
            </>
        );
    }

    if (error) {
        return (
            <>
                <HeaderOne />
                <div style={{
                    textAlign: "center",
                    padding: "50px",
                    color: "#EFD1D4",
                    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                    backgroundColor: "#EFD1D4",
                    minHeight: "400px"
                }}>
                    <p style={{ fontSize: "1.2rem" }}>{error}</p>
                </div>
                <BottomFooter />
            </>
        );
    }

    if (!aboutData) {
        return (
            <>
                <HeaderOne />
                <div style={{
                    textAlign: "center",
                    padding: "50px",
                    color: "#EFD1D4",
                    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                    backgroundColor: "#EFD1D4",
                    minHeight: "400px"
                }}>
                    <p style={{ fontSize: "1.2rem" }}>No about information available.</p>
                </div>
                <BottomFooter />
            </>
        );
    }

    return (
        <>
            <HeaderOne />

            <div className="about-container" style={{ fontFamily: "'Inter', sans-serif", backgroundColor: "#FFFFFF" }}>
                {/* Hero Section */}
                <section
                    className="about-hero"
                    style={{
                        background: "linear-gradient(135deg, #1D2A3A 0%, #2C3E50 100%)",
                        color: "white",
                        padding: "80px 20px",
                        textAlign: "center",
                        position: "relative",
                        overflow: "hidden"
                    }}
                >
                    <div className="hero-glow" style={{
                        position: "absolute",
                        top: "-50%",
                        left: "-20%",
                        width: "140%",
                        height: "200%",
                        background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
                        pointerEvents: "none"
                    }}></div>

                    <div
                        className="about-hero-content"
                        style={{ maxWidth: "1000px", margin: "0 auto", position: "relative", zIndex: 1 }}
                    >
                        <h1
                            className="about-title"
                            style={{
                                fontSize: "clamp(2.5rem, 5vw, 4rem)",
                                fontWeight: "800",
                                marginBottom: "20px",
                                color: "white",
                                textShadow: "0 2px 10px rgba(0,0,0,0.1)"
                            }}
                        >
                            {aboutData.hero_title}
                        </h1>
                        {aboutData.hero_subtitle && (
                            <p
                                className="about-subtitle"
                                style={{
                                    fontSize: "1.25rem",
                                    opacity: "0.95",
                                    lineHeight: "1.6",
                                    color: "white",
                                    maxWidth: "700px",
                                    margin: "0 auto"
                                }}
                            >
                                {aboutData.hero_subtitle}
                            </p>
                        )}
                    </div>
                </section>

                <div
                    className="about-container-inner"
                    style={{ maxWidth: "1200px", margin: "0 auto", padding: "60px 20px" }}
                >
                    {/* Expand/Collapse All Buttons */}
                    <div style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "12px",
                        marginBottom: "30px",
                        flexWrap: "wrap"
                    }}>
                        <button
                            onClick={() => toggleAllSections(true)}
                            style={{
                                backgroundColor: "rgba(29, 42, 58, 0.1)",
                                color: "#1D2A3A",
                                border: "1px solid rgba(29, 42, 58, 0.2)",
                                padding: "10px 24px",
                                borderRadius: "30px",
                                cursor: "pointer",
                                fontWeight: "600",
                                fontSize: "14px",
                                transition: "all 0.3s ease"
                            }}
                        >
                            Expand All
                        </button>
                        <button
                            onClick={() => toggleAllSections(false)}
                            style={{
                                backgroundColor: "#f8f9fa",
                                color: "#6c757d",
                                border: "1px solid #dee2e6",
                                padding: "10px 24px",
                                borderRadius: "30px",
                                cursor: "pointer",
                                fontWeight: "600",
                                fontSize: "14px",
                                transition: "all 0.3s ease"
                            }}
                        >
                            Collapse All
                        </button>
                    </div>

                    {/* Story Section */}
                    <section
                        className="about-section"
                        style={{ marginBottom: "60px" }}
                    >
                        <h2
                            className="about-section-title"
                            style={{
                                color: "#1A1A1A",
                                fontSize: "2.2rem",
                                marginBottom: "32px",
                                fontWeight: "700",
                                display: "flex",
                                alignItems: "center",
                                gap: "15px"
                            }}
                        >
                            <span style={{ width: "40px", height: "4px", backgroundColor: "#1D2A3A", borderRadius: "2px" }}></span>
                            Our Story
                        </h2>

                        <div
                            className="about-detail-card"
                            style={{
                                backgroundColor: "#FFFFFF",
                                borderRadius: "24px",
                                marginBottom: "28px",
                                border: "1px solid #E2E8F0",
                                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
                                overflow: "hidden",
                                transition: "all 0.3s ease"
                            }}
                        >
                            <div
                                className="about-detail-header"
                                style={{
                                    backgroundColor: expandedSections.story ? "#F1F5F9" : "#FFFFFF",
                                    padding: "24px 32px",
                                    borderBottom: expandedSections.story ? "1px solid #E2E8F0" : "1px solid transparent",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    cursor: "pointer"
                                }}
                                onClick={() => toggleSection("story")}
                            >
                                <h3 style={{
                                    color: expandedSections.story ? "#1D2A3A" : "#1A1A1A",
                                    margin: "0",
                                    fontSize: "1.4rem",
                                    fontWeight: "600",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px"
                                }}>
                                    <i className={`ph ${expandedSections.story ? "ph-book-open" : "ph-book"}`} style={{ fontSize: "1.6rem" }}></i>
                                    {aboutData.story_title}
                                </h3>
                                <div style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    backgroundColor: expandedSections.story ? "#1D2A3A" : "#F1F5F9",
                                    color: expandedSections.story ? "white" : "#64748B",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "all 0.3s ease",
                                    transform: expandedSections.story ? "rotate(180deg)" : "rotate(0deg)"
                                }}>
                                    <i className="ph ph-caret-down"></i>
                                </div>
                            </div>
                            <div
                                className="about-detail-content"
                                style={{
                                    padding: expandedSections.story ? "32px" : "0",
                                    color: "#4B5563",
                                    lineHeight: "1.8",
                                    backgroundColor: "#FFFFFF",
                                    maxHeight: expandedSections.story ? "5000px" : "0",
                                    opacity: expandedSections.story ? "1" : "0",
                                    overflow: "hidden",
                                    transition: "all 0.5s ease"
                                }}
                            >
                                {aboutData.story_text_1 && (
                                    <p style={{ marginBottom: "20px", fontSize: "1.1rem" }}>
                                        {aboutData.story_text_1}
                                    </p>
                                )}
                                {aboutData.story_text_2 && (
                                    <p style={{ fontSize: "1.1rem" }}>
                                        {aboutData.story_text_2}
                                    </p>
                                )}
                            </div>
                        </div>
                    </section>

                    {/* Services Section */}
                    {aboutData.service_categories && aboutData.service_categories.length > 0 && (
                        <section
                            className="about-section"
                            style={{ marginBottom: "60px" }}
                        >
                            <h2
                                className="about-section-title"
                                style={{
                                    color: "#1A1A1A",
                                    fontSize: "2.2rem",
                                    marginBottom: "32px",
                                    fontWeight: "700",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "15px"
                                }}
                            >
                                <span style={{ width: "40px", height: "4px", backgroundColor: "var(--main-600)", borderRadius: "2px" }}></span>
                                {aboutData.services_title}
                            </h2>

                            <div
                                className="about-detail-card"
                                style={{
                                    backgroundColor: "#FFFFFF",
                                    borderRadius: "24px",
                                    marginBottom: "28px",
                                    border: "1px solid #E2E8F0",
                                    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
                                    overflow: "hidden",
                                    transition: "all 0.3s ease"
                                }}
                            >
                                <div
                                    className="about-detail-header"
                                    style={{
                                        backgroundColor: expandedSections.services ? "#F0FDFA" : "#FFFFFF",
                                        padding: "24px 32px",
                                        borderBottom: expandedSections.services ? "1px solid #CCFBF1" : "1px solid transparent",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        cursor: "pointer"
                                    }}
                                    onClick={() => toggleSection("services")}
                                >
                                    <h3 style={{
                                        color: expandedSections.services ? "var(--main-600)" : "#1A1A1A",
                                        margin: "0",
                                        fontSize: "1.4rem",
                                        fontWeight: "600",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "12px"
                                    }}>
                                        <i className="ph ph-sparkle" style={{ fontSize: "1.6rem" }}></i>
                                        What We Offer
                                    </h3>
                                    <div style={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        backgroundColor: expandedSections.services ? "var(--main-600)" : "#F1F5F9",
                                        color: expandedSections.services ? "white" : "#64748B",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        transition: "all 0.3s ease",
                                        transform: expandedSections.services ? "rotate(180deg)" : "rotate(0deg)"
                                    }}>
                                        <i className="ph ph-caret-down"></i>
                                    </div>
                                </div>
                                <div
                                    className="about-detail-content"
                                    style={{
                                        padding: expandedSections.services ? "40px 32px" : "0",
                                        color: "#4B5563",
                                        lineHeight: "1.8",
                                        backgroundColor: "#FFFFFF",
                                        maxHeight: expandedSections.services ? "5000px" : "0",
                                        opacity: expandedSections.services ? "1" : "0",
                                        overflow: "hidden",
                                        transition: "all 0.5s ease"
                                    }}
                                >
                                    {aboutData.services_intro && (
                                        <p style={{
                                            marginBottom: "32px",
                                            fontSize: "1.1rem",
                                            color: "#4B5563",
                                            textAlign: "center",
                                            maxWidth: "800px",
                                            margin: "0 auto 40px"
                                        }}>
                                            {aboutData.services_intro}
                                        </p>
                                    )}

                                    <div style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                                        gap: "24px",
                                        marginTop: "20px"
                                    }}>
                                        {aboutData.service_categories.map((service, index) => (
                                            <div
                                                key={service.id || index}
                                                style={{
                                                    backgroundColor: "#F8FAFC",
                                                    borderRadius: "20px",
                                                    padding: "30px",
                                                    border: "1px solid #F1F5F9",
                                                    transition: "all 0.3s ease"
                                                }}
                                            >
                                                <div style={{
                                                    width: "50px",
                                                    height: "50px",
                                                    borderRadius: "15px",
                                                    backgroundColor: "rgba(195, 220, 216, 0.4)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: "1.5rem",
                                                    marginBottom: "20px",
                                                    color: "var(--main-600)"
                                                }}>
                                                    {service.icon || index + 1}
                                                </div>
                                                <h4 style={{
                                                    color: "#1A1A1A",
                                                    fontSize: "1.25rem",
                                                    marginBottom: "16px",
                                                    fontWeight: "700"
                                                }}>
                                                    {service.title}
                                                </h4>
                                                <ul style={{
                                                    listStyle: "none",
                                                    padding: "0",
                                                    margin: "0"
                                                }}>
                                                    {service.items.map((item, idx) => (
                                                        <li
                                                            key={item.id || idx}
                                                            style={{
                                                                color: "#64748B",
                                                                fontSize: "1rem",
                                                                marginBottom: "10px",
                                                                paddingLeft: "24px",
                                                                position: "relative"
                                                            }}
                                                        >
                                                            <i className="ph ph-check-circle" style={{
                                                                position: "absolute",
                                                                left: "0",
                                                                top: "3px",
                                                                color: "var(--main-600)",
                                                                fontSize: "1.1rem"
                                                            }}></i>
                                                            {item.name}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Store Hours Section */}
                    {aboutData.hours && aboutData.hours.length > 0 && (
                        <section
                            className="about-section"
                            style={{ marginBottom: "40px" }}
                        >
                            <h2
                                className="about-section-title"
                                style={{
                                    color: "#1A1A1A",
                                    fontSize: "2.2rem",
                                    marginBottom: "32px",
                                    fontWeight: "700",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "15px"
                                }}
                            >
                                <span style={{ width: "40px", height: "4px", backgroundColor: "#3D5E59", borderRadius: "2px" }}></span>
                                {aboutData.store_hours_title}
                            </h2>

                            <div
                                className="about-detail-card"
                                style={{
                                    backgroundColor: "#FFFFFF",
                                    borderRadius: "24px",
                                    marginBottom: "28px",
                                    border: "1px solid #E2E8F0",
                                    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
                                    overflow: "hidden",
                                    transition: "all 0.3s ease"
                                }}
                            >
                                <div
                                    className="about-detail-header"
                                    style={{
                                        backgroundColor: expandedSections.hours ? "#F8FAFC" : "#FFFFFF",
                                        padding: "24px 32px",
                                        borderBottom: expandedSections.hours ? "1px solid #F1F5F9" : "1px solid transparent",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        cursor: "pointer"
                                    }}
                                    onClick={() => toggleSection("hours")}
                                >
                                    <h3 style={{
                                        color: expandedSections.hours ? "#3D5E59" : "#1A1A1A",
                                        margin: "0",
                                        fontSize: "1.4rem",
                                        fontWeight: "600",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "12px"
                                    }}>
                                        <i className="ph ph-map-pin" style={{ fontSize: "1.6rem" }}></i>
                                        Visit Us
                                    </h3>
                                    <div style={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        backgroundColor: expandedSections.hours ? "#3D5E59" : "#F1F5F9",
                                        color: expandedSections.hours ? "white" : "#64748B",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        transition: "all 0.3s ease",
                                        transform: expandedSections.hours ? "rotate(180deg)" : "rotate(0deg)"
                                    }}>
                                        <i className="ph ph-caret-down"></i>
                                    </div>
                                </div>
                                <div
                                    className="about-detail-content"
                                    style={{
                                        padding: expandedSections.hours ? "32px" : "0",
                                        color: "#4B5563",
                                        lineHeight: "1.8",
                                        backgroundColor: "#FFFFFF",
                                        maxHeight: expandedSections.hours ? "5000px" : "0",
                                        opacity: expandedSections.hours ? "1" : "0",
                                        overflow: "hidden",
                                        transition: "all 0.5s ease"
                                    }}
                                >
                                    <div style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                                        gap: "40px"
                                    }}>
                                        <div>
                                            {aboutData.store_hours_intro && (
                                                <p style={{
                                                    marginBottom: "24px",
                                                    fontSize: "1.1rem",
                                                    color: "#4B5563"
                                                }}>
                                                    {aboutData.store_hours_intro}
                                                </p>
                                            )}

                                            {aboutData.address_line && (
                                                <div style={{
                                                    backgroundColor: "#F1F5F9",
                                                    padding: "24px",
                                                    borderRadius: "16px",
                                                    marginBottom: "24px",
                                                    display: "flex",
                                                    gap: "15px",
                                                    alignItems: "flex-start"
                                                }}>
                                                    <i className="ph-fill ph-map-pin" style={{ color: "#1D2A3A", fontSize: "1.5rem" }}></i>
                                                    <p style={{
                                                        margin: "0",
                                                        color: "#1A1A1A",
                                                        fontSize: "1.1rem",
                                                        fontWeight: "600",
                                                        lineHeight: "1.5"
                                                    }}>
                                                        {aboutData.address_line}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        <div style={{
                                            backgroundColor: "#FFFFFF",
                                            borderRadius: "20px",
                                            border: "1px solid #F1F5F9",
                                            padding: "20px",
                                            boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
                                        }}>
                                            <h4 style={{ marginBottom: "20px", fontSize: "1.2rem", fontWeight: "700", textAlign: "center", color: "#1D2A3A" }}>Business Hours</h4>
                                            <div style={{ display: "grid", gap: "2px" }}>
                                                {aboutData.hours.map((hour, index) => (
                                                    <div
                                                        key={hour.id || index}
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            padding: "12px 16px",
                                                            backgroundColor: hour.is_closed ? "#F8FAFC" : "transparent",
                                                            borderRadius: "8px",
                                                            transition: "all 0.2s ease"
                                                        }}
                                                    >
                                                        <span style={{
                                                            fontWeight: "600",
                                                            color: hour.is_closed ? "#94A3B8" : "#1A1A1A",
                                                            fontSize: "1rem"
                                                        }}>
                                                            {hour.day}
                                                        </span>
                                                        <span style={{
                                                            color: hour.is_closed ? "#94A3B8" : "#059669",
                                                            fontWeight: "700",
                                                            fontSize: "1rem"
                                                        }}>
                                                            {hour.is_closed ? 'Closed' : hour.time}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}
                </div>
            </div>

            <BottomFooter />
        </>
    );
};

export default AboutUs;